import React, { useState, useEffect } from 'react';
import { Stethoscope, Plus, Edit, Trash2, Search, RefreshCw, CheckCircle, AlertTriangle, XCircle, Eye, FileText, Archive } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/services/api';
import { Link } from 'react-router-dom';
import DocumentUpload from '@/components/DocumentUpload';
import { useApp } from '@/context/AppContext';

export default function GestionVisitesMedicales() {
  const { currentUser } = useApp();
  const [visites, setVisites] = useState([]);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [typesVisites, setTypesVisites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showArchives, setShowArchives] = useState(false);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedVisite, setSelectedVisite] = useState(null);
  const [formData, setFormData] = useState({
    collaborateur_id: '',
    type_visite_id: '',
    date_visite: '',
    date_fin: '',
    medecin: '',
    apte: true,
    restrictions: ''
  });

  useEffect(() => {
    loadData();
  }, [showArchives]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [visitesData, collabsData, typesData] = await Promise.all([
        api.getAllVisitesMedicales(currentUser?.role, showArchives),
        api.getCollaborateurs(),
        api.getTypesVisites()
      ]);
      setVisites(visitesData || []);
      setCollaborateurs(collabsData || []);
      setTypesVisites(typesData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.message?.includes('403') || error.message?.includes('non autorisé')) {
        toast.error('Accès non autorisé aux visites médicales');
      } else {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setSelectedVisite(null);
    setFormData({
      collaborateur_id: '',
      type_visite_id: '',
      date_visite: new Date().toISOString().split('T')[0],
      date_fin: '',
      medecin: '',
      apte: true,
      restrictions: ''
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (visite) => {
    setSelectedVisite(visite);
    setFormData({
      collaborateur_id: visite.collaborateur_id || '',
      type_visite_id: visite.type_visite_id || '',
      date_visite: visite.date_visite || '',
      date_fin: visite.date_fin || '',
      medecin: visite.medecin || '',
      apte: visite.apte !== false,
      restrictions: visite.restrictions || ''
    });
    setDialogOpen(true);
  };

  const calculateDateFin = (dateVisite, typeVisiteId) => {
    if (!dateVisite || !typeVisiteId) return '';
    const typeVisite = typesVisites.find(t => t.id === typeVisiteId);
    // Utiliser periodicite_mois (nom du champ API) ou duree_validite_mois pour compatibilité
    const mois = typeVisite?.periodicite_mois || typeVisite?.duree_validite_mois || 24;
    
    const date = new Date(dateVisite);
    date.setMonth(date.getMonth() + mois);
    return date.toISOString().split('T')[0];
  };

  const handleTypeChange = (typeId) => {
    const newDateFin = calculateDateFin(formData.date_visite, typeId);
    setFormData(prev => ({
      ...prev,
      type_visite_id: typeId,
      date_fin: newDateFin || prev.date_fin
    }));
  };

  const handleDateChange = (dateVisite) => {
    const newDateFin = calculateDateFin(dateVisite, formData.type_visite_id);
    setFormData(prev => ({
      ...prev,
      date_visite: dateVisite,
      date_fin: newDateFin || prev.date_fin
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.collaborateur_id || !formData.type_visite_id || !formData.date_visite) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    // Vérifier le justificatif obligatoire pour la création
    if (!selectedVisite && !formData.document) {
      toast.error('Le justificatif est obligatoire');
      return;
    }

    try {
      let visiteId;
      
      if (selectedVisite) {
        await api.updateVisiteMedicale(selectedVisite.id, formData);
        visiteId = selectedVisite.id;
        toast.success('Visite médicale modifiée');
      } else {
        const result = await api.addVisiteMedicale(formData.collaborateur_id, formData);
        visiteId = result.id;
        toast.success('Visite médicale ajoutée');
      }
      
      // Upload du document si fourni
      if (formData.document && visiteId) {
        try {
          await api.uploadDocument(formData.document, 'visite', visiteId);
          toast.success('Document joint avec succès');
        } catch (uploadError) {
          console.error('Error uploading document:', uploadError);
          toast.error('Erreur lors de l\'upload du justificatif');
          return;
        }
      }
      
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    if (!selectedVisite) return;
    try {
      await api.deleteVisiteMedicale(selectedVisite.id);
      toast.success('Visite médicale supprimée');
      setDeleteDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (visite) => {
    if (!visite.date_fin) return <Badge variant="secondary">Sans date fin</Badge>;
    
    const today = new Date();
    const dateFin = new Date(visite.date_fin);
    const daysUntilExpiry = Math.ceil((dateFin - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Expirée</Badge>;
    } else if (daysUntilExpiry <= 30) {
      return <Badge className="bg-orange-100 text-orange-800"><AlertTriangle className="h-3 w-3 mr-1" />Expire bientôt</Badge>;
    } else {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Valide</Badge>;
    }
  };

  const getCollaborateurName = (collabId) => {
    const collab = collaborateurs.find(c => c.id === collabId);
    return collab ? `${collab.prenom} ${collab.nom}` : 'Inconnu';
  };

  const filteredVisites = visites.filter(v => {
    const collabName = getCollaborateurName(v.collaborateur_id).toLowerCase();
    const matchesSearch = searchTerm === '' || 
      collabName.includes(searchTerm.toLowerCase()) ||
      v.medecin?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (statusFilter === 'all') return matchesSearch;
    
    const today = new Date();
    const dateFin = v.date_fin ? new Date(v.date_fin) : null;
    
    if (statusFilter === 'valid') {
      return matchesSearch && dateFin && dateFin > today;
    } else if (statusFilter === 'expired') {
      return matchesSearch && dateFin && dateFin <= today;
    } else if (statusFilter === 'expiring') {
      if (!dateFin) return false;
      const daysUntilExpiry = Math.ceil((dateFin - today) / (1000 * 60 * 60 * 24));
      return matchesSearch && daysUntilExpiry > 0 && daysUntilExpiry <= 30;
    }
    return matchesSearch;
  });

  // Statistics - only count non-archived items
  const activeVisites = visites.filter(v => !v.is_archived);
  const stats = {
    total: activeVisites.length,
    valid: activeVisites.filter(v => v.date_fin && new Date(v.date_fin) > new Date()).length,
    expiring: activeVisites.filter(v => {
      if (!v.date_fin) return false;
      const days = Math.ceil((new Date(v.date_fin) - new Date()) / (1000 * 60 * 60 * 24));
      return days > 0 && days <= 30;
    }).length,
    expired: activeVisites.filter(v => v.date_fin && new Date(v.date_fin) <= new Date()).length,
    archived: visites.filter(v => v.is_archived).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Stethoscope className="h-6 w-6 text-primary" />
            Visites Médicales
          </h1>
          <p className="text-gray-600 mt-1">Suivi des visites médicales des collaborateurs</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="archives-vm"
              checked={showArchives}
              onCheckedChange={setShowArchives}
            />
            <Label htmlFor="archives-vm" className="text-sm flex items-center gap-1 cursor-pointer">
              <Archive className="h-4 w-4" />
              Voir les archives
            </Label>
          </div>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle visite
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100">
                <Stethoscope className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Valides</p>
                <p className="text-xl font-bold text-green-600">{stats.valid}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-orange-100">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Expirent bientôt</p>
                <p className="text-xl font-bold text-orange-600">{stats.expiring}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-gray-100">
                {showArchives ? <Archive className="h-5 w-5 text-gray-600" /> : <XCircle className="h-5 w-5 text-red-600" />}
              </div>
              <div>
                <p className="text-sm text-gray-500">{showArchives ? 'Archivées' : 'Expirées'}</p>
                <p className="text-xl font-bold text-gray-600">{showArchives ? stats.archived : stats.expired}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par collaborateur, médecin..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="valid">Valides</SelectItem>
                <SelectItem value="expiring">Expirent bientôt</SelectItem>
                <SelectItem value="expired">Expirées</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des visites médicales</CardTitle>
          <CardDescription>{filteredVisites.length} visite(s) trouvée(s)</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {filteredVisites.length === 0 ? (
            <div className="text-center py-12">
              <Stethoscope className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">Aucune visite médicale trouvée</p>
              <Button variant="link" onClick={handleOpenCreate}>
                Ajouter une visite médicale
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Collaborateur</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date visite</TableHead>
                  <TableHead>Date fin</TableHead>
                  <TableHead>Médecin</TableHead>
                  <TableHead>Aptitude</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVisites.map((visite) => (
                  <TableRow key={visite.id} className={visite.is_archived ? 'bg-gray-50 opacity-70' : ''}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Link to={`/collaborateurs/${visite.collaborateur_id}`} className="hover:text-primary">
                          {getCollaborateurName(visite.collaborateur_id)}
                        </Link>
                        {visite.is_archived && (
                          <Badge variant="secondary" className="text-xs">
                            <Archive className="h-3 w-3 mr-1" />
                            Archivée
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{visite.type_visite_libelle || 'N/A'}</TableCell>
                    <TableCell>
                      {visite.date_visite ? new Date(visite.date_visite).toLocaleDateString('fr-FR') : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {visite.date_fin ? new Date(visite.date_fin).toLocaleDateString('fr-FR') : 'N/A'}
                    </TableCell>
                    <TableCell>{visite.medecin || 'N/A'}</TableCell>
                    <TableCell>
                      {visite.apte !== false ? (
                        <Badge className="bg-green-100 text-green-800">Apte</Badge>
                      ) : (
                        <Badge variant="destructive">Inapte</Badge>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(visite)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          title="Documents"
                          onClick={() => { setSelectedVisite(visite); setDocumentsDialogOpen(true); }}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(visite)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-red-600"
                          onClick={() => { setSelectedVisite(visite); setDeleteDialogOpen(true); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedVisite ? 'Modifier la visite médicale' : 'Nouvelle visite médicale'}
            </DialogTitle>
            <DialogDescription>
              {selectedVisite ? 'Modifier les informations de la visite' : 'Ajouter une nouvelle visite médicale'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Collaborateur *</Label>
                <Select 
                  value={formData.collaborateur_id} 
                  onValueChange={(value) => setFormData({ ...formData, collaborateur_id: value })}
                  disabled={!!selectedVisite}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un collaborateur" />
                  </SelectTrigger>
                  <SelectContent>
                    {collaborateurs.map((collab) => (
                      <SelectItem key={collab.id} value={collab.id}>
                        {collab.prenom} {collab.nom} - {collab.matricule}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type de visite *</Label>
                <Select 
                  value={formData.type_visite_id} 
                  onValueChange={handleTypeChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typesVisites.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.libelle} ({type.periodicite_mois || type.duree_validite_mois || 24} mois)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de visite *</Label>
                  <Input
                    type="date"
                    value={formData.date_visite}
                    onChange={(e) => handleDateChange(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin</Label>
                  <Input
                    type="date"
                    value={formData.date_fin}
                    onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">Calculée automatiquement</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Médecin</Label>
                <Input
                  value={formData.medecin}
                  onChange={(e) => setFormData({ ...formData, medecin: e.target.value })}
                  placeholder="Dr. Martin"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="apte"
                  checked={formData.apte}
                  onCheckedChange={(checked) => setFormData({ ...formData, apte: checked })}
                />
                <Label htmlFor="apte">Apte au poste de travail</Label>
              </div>
              {!formData.apte && (
                <div className="space-y-2">
                  <Label>Restrictions</Label>
                  <Textarea
                    value={formData.restrictions}
                    onChange={(e) => setFormData({ ...formData, restrictions: e.target.value })}
                    placeholder="Précisez les restrictions..."
                    rows={2}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="document">Justificatif *</Label>
                <Input
                  id="document"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setFormData({ ...formData, document: e.target.files[0] })}
                  className="cursor-pointer"
                  required={!selectedVisite}
                />
                <p className="text-xs text-gray-500">Format accepté: PDF, JPG, PNG (obligatoire)</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {selectedVisite ? 'Modifier' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la visite médicale</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer cette visite médicale ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Documents Dialog */}
      <Dialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Documents - {selectedVisite && getCollaborateurName(selectedVisite.collaborateur_id)}
            </DialogTitle>
            <DialogDescription>
              Visite du {selectedVisite?.date_visite ? new Date(selectedVisite.date_visite).toLocaleDateString('fr-FR') : 'N/A'}
            </DialogDescription>
          </DialogHeader>
          {selectedVisite && (
            <DocumentUpload 
              entityType="visite" 
              entityId={selectedVisite.id}
              title="Documents de la visite médicale"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
